import React from 'react';
import styled, { css } from 'styled-components';
import {
  breakpointSizes,
  minBreakpointQuery,
  sectionMargins,
  brandColours,
  brandFonts,
  fontSize,
  fluidFontSize,
} from '../styles';
import { Container, Heading, Slider, Image } from './ui';

const StyledTestimonials = styled.section`
  ${sectionMargins()};
  text-align: center;
`;

const StyledInner = styled.div`
  position: relative;
  margin-top: 40px;

  ${minBreakpointQuery.small`
    margin-top: 50px;
  `}

  ${minBreakpointQuery.large`
    margin-top: 60px;
  `}

  &:before,
  &:after {
    content: '';
    position: absolute;
    left: 0;
    height: 1px;
    width: 100%;
    background-color: ${brandColours.primary};
    opacity: 0.1;
  }
`;

const commonItemsStyles = () => {
  return css`
    position: relative;
    margin: 0 auto;
    max-width: 740px;

    &:before {
      content: '“';
      position: absolute;
      top: 0;
      left: 50%;
      color: ${brandColours.quinary};
      ${brandFonts.secondary};
      ${fontSize(160)}
      line-height: 1;
      transform: translateX(-50%) translateY(-25%);
    }
  `;
};

const StyledItems = styled.div`
  ${commonItemsStyles()};
`;

const StyledItemsSlider = styled(Slider)`
  ${commonItemsStyles()};

  .slick-dots {
    bottom: 15px;

    ${minBreakpointQuery.small`
      bottom: 20px;
    `}

    ${minBreakpointQuery.medium`
      bottom: 25px;
    `}

    ${minBreakpointQuery.large`
      bottom: 30px;
    `}
  }
`;

const StyledTestimonial = styled.figure`
  margin: 0;
  padding: 40px 0;

  ${minBreakpointQuery.small`
    padding-top: 50px;
    padding-bottom: 50px;
  `}

  ${minBreakpointQuery.medium`
    padding-top: 60px;
    padding-bottom: 60px;
  `}

  ${minBreakpointQuery.large`
    padding-top: 70px;
    padding-bottom: 70px;
  `}
`;

const StyledBlockquote = styled.blockquote`
  margin: 0;
`;

const StyledQuote = styled.p`
  ${brandFonts.secondary};
  ${fluidFontSize(
    '22px',
    '35px',
    breakpointSizes.tiny,
    breakpointSizes.xxlarge
  )};
  line-height: 1.2;
`;

const StyledAuthor = styled.div`
  margin-top: 25px;

  ${minBreakpointQuery.small`
    margin-top: 30px;
  `}

  ${minBreakpointQuery.medium`
    margin-top: 35px;
  `}

  ${minBreakpointQuery.large`
    margin-top: 40px;
  `}
`;

const StyledImage = styled(Image)`
  margin-bottom: 10px;
  border-radius: 50%;
`;

const StyledName = styled.p`
  color: ${brandColours.quinary};
  ${fontSize(14)};
`;

const StyledMeta = styled.p`
  margin-top: 5px;
  ${fontSize(14)};
`;

const Testimonials = ({ heading, items }) => {
  const ItemsComponent =
    items && items.length > 1 ? StyledItemsSlider : StyledItems;
  const sliderOptions = {
    arrows: false,
    dots: true,
  };

  return (
    items &&
    items.length > 0 && (
      <StyledTestimonials>
        <Container narrow={true}>
          {heading && <Heading>{heading}</Heading>}
          <StyledInner>
            <ItemsComponent {...sliderOptions}>
              {items.map(({ text, image, name, meta }, i) => (
                <StyledTestimonial key={`testimonial-${i}`}>
                  <StyledBlockquote>
                    <StyledQuote>{text}</StyledQuote>
                  </StyledBlockquote>
                  <StyledAuthor>
                    {image && (
                      <StyledImage
                        src={image.url}
                        alt={image.title}
                        width={70}
                        height={70}
                        layout="constrained"
                      />
                    )}
                    <StyledName>{name}</StyledName>
                    {meta && <StyledMeta>{meta}</StyledMeta>}
                  </StyledAuthor>
                </StyledTestimonial>
              ))}
            </ItemsComponent>
          </StyledInner>
        </Container>
      </StyledTestimonials>
    )
  );
};

export default Testimonials;
